import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import useCanvasStore from '@/store/canvasStore';
import useUiStore from '@/store/uiStore';
import { useAuth } from '@/contexts/AuthContext';
import { Send, Zap } from 'lucide-react';

function FormattedText({ text }) {
  if (!text) return null;
  const parts = text.split('\n');
  return (
    <div className="space-y-1.5">
      {parts.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        // Bold: **text**
        let formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
        // Inline code: `code`
        formatted = formatted.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono">$1</code>');
        // Bullet points
        if (/^\s*[-*]\s/.test(line)) {
          const content = line.replace(/^\s*[-*]\s/, '');
          let fc = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
          fc = fc.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono">$1</code>');
          return (
            <div key={i} className="flex gap-1.5 ml-1">
              <span className="text-cobalt mt-1 shrink-0 text-[8px]">&#9679;</span>
              <span dangerouslySetInnerHTML={{ __html: fc }} />
            </div>
          );
        }
        // Numbered lists
        const numMatch = line.match(/^\s*(\d+)[.)]\s/);
        if (numMatch) {
          const content = line.replace(/^\s*\d+[.)]\s/, '');
          let fc = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
          fc = fc.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-muted rounded text-[11px] font-mono">$1</code>');
          return (
            <div key={i} className="flex gap-1.5 ml-1">
              <span className="text-muted-foreground font-mono text-[10px] mt-0.5 shrink-0 w-3 text-right">{numMatch[1]}.</span>
              <span dangerouslySetInnerHTML={{ __html: fc }} />
            </div>
          );
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: formatted }} />;
      })}
    </div>
  );
}

export default function AskTab({ nodeId, nodeLabel, nodeDescription }) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const { nodeChats, addMessage } = useCanvasStore();
  const { openUpgradeModal } = useUiStore();
  const { user, refreshUser } = useAuth();
  const messages = nodeChats[nodeId] || [];

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!message.trim() || loading) return;
    const userMsg = { id: Date.now().toString(), role: 'user', content: message, timestamp: new Date() };
    addMessage(nodeId, userMsg);
    setMessage('');
    setLoading(true);
    try {
      const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
      const res = await api.post('/ai/chat', { nodeLabel, nodeDescription: nodeDescription || '', message: userMsg.content, history });
      addMessage(nodeId, {
        id: (Date.now() + 1).toString(), role: 'assistant',
        content: res.data.response, timestamp: new Date()
      });
      refreshUser();
    } catch (err) {
      if (err.response?.status === 403) openUpgradeModal();
      else addMessage(nodeId, {
        id: (Date.now() + 1).toString(), role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.', timestamp: new Date()
      });
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="ask-tab">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8" data-testid="ask-empty">
            <p className="font-body text-sm text-muted-foreground">Ask anything about</p>
            <p className="font-body text-sm font-medium mt-0.5">{nodeLabel}</p>
            {nodeDescription && (
              <p className="font-body text-[11px] text-muted-foreground/60 mt-1 max-w-[200px] mx-auto">{nodeDescription}</p>
            )}
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.id}`}>
            {msg.role === 'user' ? (
              <div className="max-w-[85%] px-3 py-2 rounded-lg rounded-br-sm bg-cobalt text-white text-sm font-body leading-relaxed">
                {msg.content}
              </div>
            ) : (
              <div className="max-w-[90%] px-3.5 py-2.5 rounded-lg rounded-bl-sm bg-card border border-border text-sm font-body leading-relaxed">
                <FormattedText text={msg.content} />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start" data-testid="chat-loading">
            <div className="px-3.5 py-2.5 bg-card border border-border rounded-lg rounded-bl-sm">
              <div className="flex gap-1.5 items-center">
                <span className="w-1.5 h-1.5 bg-cobalt/40 rounded-full animate-pulse" />
                <span className="w-1.5 h-1.5 bg-cobalt/40 rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                <span className="w-1.5 h-1.5 bg-cobalt/40 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Credits indicator for all users */}
      <div className="px-4 py-1.5 border-t border-border bg-muted/30 flex items-center justify-between" data-testid="credits-bar">
        <div className="flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-cobalt" strokeWidth={1.5} />
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
            {user?.credits ?? 0} credit{user?.credits !== 1 ? 's' : ''} left
          </span>
        </div>
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2">
          <input
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
            placeholder="Ask a question..."
            className="flex-1 text-sm font-body bg-muted rounded-md px-3 py-2 outline-none border border-border focus:border-cobalt transition-colors"
            data-testid="ask-input"
          />
          <button
            onClick={handleSend}
            disabled={!message.trim() || loading}
            className="p-2 rounded-md bg-cobalt text-white hover:bg-cobalt/90 disabled:opacity-40 transition-colors"
            data-testid="ask-send-btn"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
